import React, { useMemo, useState, useEffect } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Panel from '../../../components/Panel'
import Button from '../../../components/Form/Button'
import Input from '../../../components/Form/Input'
import Table, { TableColumnConfig } from '../../../components/Table/Table'
import { CustomContentModal } from '../../../components/CustomContentModal'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import {
  activateVNEMAllocationApi,
  getProgramMeterOptionsApi,
  getVNEMAllocationsApi,
  listProgramMetersApi,
  retireProgramMeterApi,
} from '../../../apis/apis'
import { getApiErrorMessage } from '../../../apis/apiError'
import { VNEMAllocationSubmission, GetVNEMAllocationsResponse, ProgramMeterOption, ProgramMeterRow } from '../../../apis/types'
import EditVNEMPercentagesModal from './EditVNEMPercentagesModal'
import { formatUTCDate, formatUTCDetailedDate, isEndDateOnOrAfterStart, utcTodayDateOnlyString } from '../../../util/string'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import classNames from 'classnames'
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { XCircleIcon } from '@heroicons/react/24/outline'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import dayjs from 'dayjs'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import ProgramMeterDatesModal from './ProgramMeterDatesModal'
import ModifiedDescriptionField from '../programOnboarding/ModifiedDescriptionField'

interface VNEMAllocationManagerProps {
  programId: string
  onRefresh?: () => void
  /** Gates the merged program-meter dates block (Add meter / Edit dates / Remove). Defaults read-only. */
  canModifyMeters?: boolean
  /** Gates percentage edits on the active-at-utility submission (WEB-7253). Defaults read-only. */
  canModifyVnem?: boolean
}

type ProgramMeterRowWithId = ProgramMeterRow & { id: string }

const METER_FALLBACK_ERROR = 'Something went wrong. Please try again.'

interface RetireProgramMeterValues {
  endDate: string
  modifiedDescription: string
}

interface RetireProgramMeterModalProps {
  isOpen: boolean
  row: ProgramMeterRow
  programId: string
  onClose: () => void
  onRetired: () => void
}

const RetireProgramMeterModal: React.FC<RetireProgramMeterModalProps> = ({
  isOpen,
  row,
  programId,
  onClose,
  onRetired,
}) => {
  const dispatch = useAppDispatch()
  const [apiError, setApiError] = useState<string | null>(null)

  const formik = useFormik<RetireProgramMeterValues>({
    initialValues: { endDate: utcTodayDateOnlyString(), modifiedDescription: '' },
    enableReinitialize: true,
    validationSchema: Yup.object({
      endDate: Yup.string().required('Required'),
      modifiedDescription: Yup.string().required('Required'),
    }),
    validate: (values) => {
      const errors: Partial<Record<keyof RetireProgramMeterValues, string>> = {}
      if (!isEndDateOnOrAfterStart(values.endDate, row.startDate)) {
        errors.endDate = 'End date cannot be before start date'
      }
      return errors
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      setApiError(null)
      setSubmitting(true)
      try {
        await retireProgramMeterApi(programId, row.cuid, {
          endDate: values.endDate,
          modifiedDescription: values.modifiedDescription,
        })
        await dispatch(pushToast({ type: 'success', message: 'Program meter end-dated' }))
        onRetired()
        onClose()
      } catch (e: unknown) {
        setApiError(getApiErrorMessage(e, METER_FALLBACK_ERROR))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (isOpen) {
      setApiError(null)
      void formik.resetForm({ values: { endDate: utcTodayDateOnlyString(), modifiedDescription: '' } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, row.cuid])

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-2 text-xl font-medium dark:text-white">End program meter?</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          This end-dates {row.benefittingMeter.meterNumber} on this program. The row stays in history.
        </p>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <Input
            label="Final end date"
            type="date"
            required
            min={row.startDate}
            value={formik.values.endDate}
            onChange={(e) => formik.setFieldValue('endDate', e.target.value)}
            error={formik.errors.endDate}
          />
          <ModifiedDescriptionField
            value={formik.values.modifiedDescription}
            onChange={(value) => formik.setFieldValue('modifiedDescription', value)}
            error={formik.errors.modifiedDescription}
          />
          {apiError != null && <p className="text-sm text-red-600 dark:text-red-500">{apiError}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isSaving={formik.isSubmitting}>
              End program meter
            </Button>
          </div>
        </form>
      </div>
    </CustomContentModal>
  )
}

const VNEMAllocationManager: React.FC<VNEMAllocationManagerProps> = ({
  programId,
  onRefresh,
  canModifyMeters = false,
  canModifyVnem = false,
}) => {
  const [allocationsData, setAllocationsData] = useState<GetVNEMAllocationsResponse | null>(null)
  const [selectedActionedAllocation, setSelectedActionedAllocation] = useState<VNEMAllocationSubmission | null>(null)
  const [selectedNonActionedSubmission, setSelectedNonActionedSubmission] = useState<VNEMAllocationSubmission | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showActivateModal, setShowActivateModal] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState('')
  const [isActivating, setIsActivating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [meters, setMeters] = useState<ProgramMeterRow[] | null>(null)
  const [meterOptions, setMeterOptions] = useState<ProgramMeterOption[]>([])
  const [datesModal, setDatesModal] = useState<{
    mode: 'create' | 'edit'
    row: ProgramMeterRow | null
    initialBenefittingMeterCuid?: string
  } | null>(null)
  const [retireMeterRow, setRetireMeterRow] = useState<ProgramMeterRow | null>(null)
  const [editingPercentages, setEditingPercentages] = useState<VNEMAllocationSubmission | null>(null)
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)

  const hasActivatePermission = permissions?.includes('program:manager:vnem:activate') ?? false

  const fetchMeters = async (): Promise<void> => {
    const rows = await listProgramMetersApi(programId)
    setMeters(rows)
  }

  const fetchAllocations = async (signal?: AbortSignal): Promise<void> => {
    try {
      const response = await getVNEMAllocationsApi(programId)

      // Check if component was unmounted
      if (signal?.aborted) return

      try {
        await fetchMeters()
      } catch (meterError: unknown) {
        const err = meterError as { response?: { data?: { message?: string } }; message?: string }
        await dispatch(
          pushToast({
            type: 'error',
            message: 'Error fetching program meters',
            description: `${err.response?.data?.message ?? err.message ?? 'Unknown error'}`,
          })
        )
      }

      // Auto-select the first future allocation if available
      if (response.submissions.length > 0) {
        const submissions = [...response.submissions].sort(
          (a, b) => new Date(b.submissionDate).getTime() - new Date(a.submissionDate).getTime()
        )
        setAllocationsData({ ...response, submissions })
        setSelectedActionedAllocation(
          submissions.find((s) => s.isActiveAtUtility && s.utilityEffectiveDate !== null && s.utilityEndDate !== null) ??
            submissions[0]
        )
        setSelectedNonActionedSubmission(
          submissions.find((s) => !s.isActiveAtUtility && s.utilityEffectiveDate === null && s.utilityEndDate === null) ??
            submissions[0]
        )
      } else {
        setAllocationsData(response)
      }

      setIsLoading(false)
    } catch (e: unknown) {
      // Check if component was unmounted
      if (signal?.aborted) return

      const error = e as { response?: { data?: { message?: string } }; message?: string }
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Error fetching VNEM allocations',
          description: `${error.response?.data?.message ?? error.message ?? 'Unknown error'}`,
        })
      )
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const abortController = new AbortController()
    void fetchAllocations(abortController.signal)
    if (canModifyMeters) {
      void getProgramMeterOptionsApi(programId)
        .then(setMeterOptions)
        .catch(async (e: unknown) => {
          const err = e as { response?: { data?: { message?: string } }; message?: string }
          await dispatch(
            pushToast({
              type: 'error',
              message: 'Error loading meter options',
              description: `${err.response?.data?.message ?? err.message ?? 'Unknown error'}`,
            })
          )
        })
    } else {
      setMeterOptions([])
    }

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, canModifyMeters])

  useEffect(() => {
    setSearchTerm('')
  }, [programId])

  const handleActivateAllocation = async (): Promise<void> => {
    if (!selectedNonActionedSubmission || !effectiveDate) {
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Please select an effective date',
        })
      )
      return
    }

    setIsActivating(true)
    try {
      await activateVNEMAllocationApi(programId, selectedNonActionedSubmission.cuid, effectiveDate)
      await dispatch(
        pushToast({
          type: 'success',
          message: 'VNEM allocation activated successfully',
        })
      )
      setShowActivateModal(false)
      setEffectiveDate('')
      await fetchAllocations()
      if (onRefresh) {
        onRefresh()
      }
    } catch (e: unknown) {
      const error = e as { response?: { data?: { message?: string } }; message?: string }
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Error activating VNEM allocation',
          description: `${error.response?.data?.message ?? error.message ?? 'Unknown error'}`,
        })
      )
    } finally {
      setIsActivating(false)
    }
  }

  const meterOptionByCuid = useMemo(
    () => new Map(meterOptions.map((m) => [m.cuid, m])),
    [meterOptions]
  )

  const programMeterForCard = (benefittingMeterCuid: string): ProgramMeterRow | undefined => {
    const rows = (meters ?? []).filter((m) => m.benefittingMeter.cuid === benefittingMeterCuid)
    if (rows.length === 0) {
      return undefined
    }
    const inEffect = rows.find((m) => m.isInEffect)
    if (inEffect != null) {
      return inEffect
    }
    return [...rows].sort((a, b) => b.startDate.localeCompare(a.startDate))[0]
  }

  const renderProgramMeterBlock = (benefittingMeterCuid: string): React.ReactElement => {
    const programMeter = programMeterForCard(benefittingMeterCuid)
    const hasMeterOption = meterOptionByCuid.has(benefittingMeterCuid)
    const showSuperseded = programMeter != null && !programMeter.isInEffect
    return (
      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-4">
        <div className="flex gap-6 text-sm">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Program Meter Start</div>
            <div className="font-medium">{programMeter != null ? formatUTCDate(programMeter.startDate) : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Program Meter End</div>
            <div className="font-medium">{programMeter != null ? formatUTCDate(programMeter.endDate) : '—'}</div>
          </div>
          {programMeter == null && (
            <div className="self-center text-xs italic text-slate-400 dark:text-slate-500">Not a program meter</div>
          )}
          {showSuperseded && (
            <div className="self-center text-xs italic text-amber-700 dark:text-amber-400">Program meter tied to this allocation is no longer active (end date is in the past)</div>
          )}
        </div>
        {canModifyMeters && programMeter != null && programMeter.isInEffect && (
          <div className="flex gap-2">
            <Button size={1} color="slate" onClick={() => setDatesModal({ mode: 'edit', row: programMeter })}>
              Edit dates
            </Button>
            <Button size={1} color="red" onClick={() => setRetireMeterRow(programMeter)}>
              Remove
            </Button>
          </div>
        )}
        {canModifyMeters && programMeter == null && hasMeterOption && (
          <Button
            size={1}
            onClick={() =>
              setDatesModal({ mode: 'create', row: null, initialBenefittingMeterCuid: benefittingMeterCuid })
            }
          >
            Add meter
          </Button>
        )}
      </div>
    )
  }

  const renderAllocationDetails = (
    allocation: VNEMAllocationSubmission,
    activation?: { canActivate: boolean, programBillingOn: boolean, hasActivatePermission: boolean }
  ) => {
    const totalPercentage = allocation.vnemAllocations.reduce((sum, alloc) => sum + Number(alloc.percentage), 0)

    const isActiveAtUtility = allocation.isActiveAtUtility && allocation.utilityEffectiveDate !== null && allocation.utilityEndDate !== null
    const isInactiveAtUtility = !allocation.isActiveAtUtility && allocation.utilityEffectiveDate !== null && allocation.utilityEndDate !== null

    const titleComponent = (
      <div className={"grid grid-cols-2 gap-x-2 w-max-content font-normal text-slate-800 dark:text-slate-300"}>
        <div className="grid grid-cols-2 gap-x-2 h-max-content w-min-content" style={{ gridTemplateColumns: 'max-content max-content' }}>
          <div>Submission Date:</div>
          <div className="font-medium">{formatUTCDetailedDate(new Date(allocation.submissionDate))}</div>

          {allocation.utilityEffectiveDate ? (
            <>
              <div>Effective Date:</div>
              <div className="font-medium">
                {formatUTCDetailedDate(allocation.utilityEffectiveDate ?? undefined)} -{' '}
                {dayjs(allocation.utilityEndDate).isSame(dayjs(new Date('2030-12-31')))
                  ? 'Current'
                  : formatUTCDetailedDate(allocation.utilityEndDate ?? undefined)}
              </div>
            </>
          ) : null}

          <div>Total Allocation:</div>
          <div
            className={classNames('font-medium', Math.round(totalPercentage) === 100 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400')}
          >
            {totalPercentage.toFixed(2)}%
          </div>
        </div>

        {isActiveAtUtility && (
          <div className="flex flex-col items-end gap-2 self-center text-right w-min-content">
            <div className="font-medium text-green-600 dark:text-green-400">
              <CheckCircleIcon className="w-5 inline-block mr-1" />
              Active at utility
            </div>
            {allocation.hasLocalEdits === true && (
              <div
                className="text-xs font-medium text-amber-700 dark:text-amber-400"
                title={
                  allocation.lastLocalEditAt != null
                    ? `Last local edit ${formatUTCDetailedDate(new Date(allocation.lastLocalEditAt))}`
                    : undefined
                }
              >
                Edited in OneBill
              </div>
            )}
            {canModifyVnem && (
              <Button size={1} color="slate" onClick={() => setEditingPercentages(allocation)}>
                Edit allocations
              </Button>
            )}
          </div>
        )}
        {isInactiveAtUtility && (
          <div className="font-medium text-red-600 dark:text-red-400 self-center text-right">
            <XCircleIcon className="w-5 inline-block mr-1" />
            Inactive at utility
          </div>
        )}

        {/* Show dropdown when there are multiple allocations, regardless of activation permissions */}
        {(activation?.canActivate) && (
          <div className="flex flex-col self-center">
            <Button color="slate" onClick={() => setShowActivateModal(true)} disabled={isActivating}>
              Set Submission Active...
            </Button>
          </div>
        )}
        {activation?.hasActivatePermission && !activation?.programBillingOn &&
          <div className="flex flex-col justify-center text-red-600 dark:text-red-400 font-medium text-right">
            Program must be live to set submission active
          </div>
        }
      </div>
    )

    const term = searchTerm.trim().toLowerCase()
    const filteredAllocations = term
      ? allocation.vnemAllocations.filter((alloc) =>
          alloc.utilityAccountName?.toLowerCase().includes(term) ||
          alloc.utilityAccountNumber?.toLowerCase().includes(term) ||
          alloc.serviceAddress?.toLowerCase().includes(term) ||
          alloc.serviceNumber?.toLowerCase().includes(term) ||
          alloc.meterNumber?.toLowerCase().includes(term)
        )
      : allocation.vnemAllocations
    const displayAllocations = [...filteredAllocations].sort((a, b) => b.percentage - a.percentage)

    return (
      <Panel header={titleComponent}>
        {displayAllocations
          .map((alloc) => (
            <div key={alloc.cuid} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 mb-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Utility Account Name</div>
                    <div className="text-sm font-medium">{alloc.utilityAccountName}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Utility Account Number</div>
                    <div className="text-sm font-medium">{alloc.utilityAccountNumber}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Service Address</div>
                    <div className="text-sm font-medium">{alloc.serviceAddress}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Service Agreement</div>
                    <div className="text-sm font-medium">{alloc.serviceNumber}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Meter Number</div>
                    <div className="text-sm font-medium">{alloc.meterNumber}</div>
                  </div>
                  {alloc.unallocatedCreditAccount && (
                    <div className="mt-2 flex items-center gap-1 text-blue-600 dark:text-blue-400">
                      <CheckCircleIcon className="w-4 h-4" />
                      <span className="text-xs">Unallocated Credit Meter</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Allocation Percentage</div>
                    <div className="text-sm font-medium">{Number(alloc.percentage).toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Tariff</div>
                    <div className="text-sm font-medium">{alloc.tariffSchedule}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Interval Data Range</div>
                    <div className="text-sm font-medium">
                      {alloc.smdIntervalDataRange?.minDate && alloc.smdIntervalDataRange?.maxDate ? (
                        <span className="text-green-600 dark:text-green-400">
                          {formatUTCDetailedDate(new Date(alloc.smdIntervalDataRange.minDate))} -{' '}
                          {formatUTCDetailedDate(new Date(alloc.smdIntervalDataRange.maxDate))}
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">No Interval Data</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {renderProgramMeterBlock(alloc.meterCuid)}

              {alloc.previousAllocation && (
                <Disclosure>
                  {({ open }) => (
                    <div className="mt-4">
                      <DisclosureButton className={classNames(
                        'flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                      )}>
                        <ChevronDownIcon className={classNames('w-4 h-4 transition-transform', open ? 'rotate-180' : '')} />
                        Previous Allocation
                      </DisclosureButton>
                      <DisclosurePanel className="mt-2 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Utility Account Name</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.utilityAccountName}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Utility Account Number</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.utilityAccountNumber}</div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Service Address</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.serviceAddress}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Service Agreement</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.serviceNumber}</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Meter Number</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.meterNumber}</div>
                            </div>
                            {alloc.previousAllocation?.unallocatedCreditAccount && (
                              <div className="mt-2 flex items-center gap-1 text-blue-600 dark:text-blue-400">
                                <CheckCircleIcon className="w-4 h-4" />
                                <span className="text-xs">Unallocated Credit Meter</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Allocation Percentage</div>
                              <div className="text-sm font-medium">{Number(alloc.previousAllocation?.percentage).toFixed(2)}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">Tariff</div>
                              <div className="text-sm font-medium">{alloc.previousAllocation?.tariffSchedule}</div>
                            </div>
                          </div>
                        </div>
                      </DisclosurePanel>
                    </div>
                  )}
                </Disclosure>
              )}
            </div>
          ))}
      </Panel>
    )
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-center">Loading VNEM allocation table submissions...</div>
      </div>
    )
  }

  if (!allocationsData) {
    return null
  }

  const { programBillingOn } = allocationsData

  const actionedSubmissions = [...allocationsData.submissions]
    .filter((s) => s.utilityEffectiveDate !== null && s.utilityEndDate !== null)
    .sort((a, b) => new Date(b.submissionDate).getTime() - new Date(a.submissionDate).getTime())

  const nonActionedSubmissions = [...allocationsData.submissions]
    .filter((s) => !s.isActiveAtUtility && s.utilityEffectiveDate === null && s.utilityEndDate === null)
    .sort((a, b) => new Date(b.submissionDate).getTime() - new Date(a.submissionDate).getTime())

  // Program meters merged into this table read their dates off whichever submissions are
  // currently displayed; anything not referenced by either stays visible below so no row
  // becomes invisible just because it isn't on the selected submission.
  const displayedMeterCuids = new Set<string>()
  ;[selectedActionedAllocation ?? actionedSubmissions[0], selectedNonActionedSubmission ?? nonActionedSubmissions[0]]
    .filter((s): s is VNEMAllocationSubmission => s != null)
    .forEach((s) => s.vnemAllocations.forEach((alloc) => displayedMeterCuids.add(alloc.meterCuid)))

  const orphanMeters = (meters ?? []).filter((m) => !displayedMeterCuids.has(m.benefittingMeter.cuid))

  const orphanMeterColConfig: Array<TableColumnConfig<ProgramMeterRowWithId>> = [
    {
      label: 'Benefitting Meter',
      render: (row) => (
        <div>
          <div className="font-medium">{row.benefittingMeter.meterNumber}</div>
          {row.benefittingMeter.serviceAddress != null && (
            <div className="text-xs text-slate-500 dark:text-slate-400">{row.benefittingMeter.serviceAddress}</div>
          )}
        </div>
      ),
    },
    {
      label: 'Start Date',
      render: (row) => formatUTCDate(row.startDate),
    },
    {
      label: 'End Date',
      render: (row) => formatUTCDate(row.endDate),
    },
  ]
  if (canModifyMeters) {
    orphanMeterColConfig.push({
      label: 'Actions',
      skipRowLink: true,
      render: (row) => (
        <div className="flex gap-2">
          <Button size={1} color="slate" onClick={() => setDatesModal({ mode: 'edit', row })}>
            Edit dates
          </Button>
          <Button size={1} color="red" onClick={() => setRetireMeterRow(row)}>
            Remove
          </Button>
        </div>
      ),
    })
  }

  return (
    <>
      <CustomContentModal
        isOpen={showActivateModal}
        onClose={() => {
          setShowActivateModal(false)
          setEffectiveDate('')
        }}
      >
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">Set Submission Active</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Please select the effective date for this submission to become active at the utility. The current active submission (if any) will be ended one day before this date.
          </p>
          <div className="mb-4">
            <label htmlFor="effectiveDate" className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
              Effective Date
            </label>
            <input
              type="date"
              id="effectiveDate"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              min={
                selectedNonActionedSubmission?.submissionDate
                  ? new Date(selectedNonActionedSubmission.submissionDate).toISOString().split('T')[0]
                  : new Date().toISOString().split('T')[0]
              }
            />
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              color="slate"
              onClick={() => {
                setShowActivateModal(false)
                setEffectiveDate('')
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleActivateAllocation()} disabled={!effectiveDate || isActivating}>
              {isActivating ? 'Activating...' : 'Confirm'}
            </Button>
          </div>
        </div>
      </CustomContentModal>

      <div className="mb-4 flex items-center gap-3">
        <input
          aria-label="Search allocations"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by account name, account number, address, service agreement, or meter number..."
          className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm placeholder-slate-400 dark:placeholder-slate-500"
        />
        {canModifyMeters && (
          <Button size={1} onClick={() => setDatesModal({ mode: 'create', row: null })}>
            Add meter
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Actioned Submissions */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="font-semibold text-md my-4">Actioned VNEM Allocation Table Submission</div>
            {actionedSubmissions.length > 1 && (
              <div className="mb-2 max-w">
                <ComboboxAutocomplete
                  placeholder="Select a submission"
                  value={selectedActionedAllocation?.cuid ?? ''}
                  handleChange={(value) => {
                    const selected = actionedSubmissions?.find((alloc) => alloc.cuid === value)
                    setSelectedActionedAllocation(selected ?? null)
                  }}
                  data={actionedSubmissions.map((s) => ({
                    id: s.cuid,
                    name: formatUTCDetailedDate(new Date(s.submissionDate)),
                    description: (
                      <div>
                        <div className="inline-block mr-2">
                          Effective {formatUTCDetailedDate(s.utilityEffectiveDate ?? undefined)} - {dayjs(s.utilityEndDate).isSame(dayjs(new Date('2030-12-31'))) ? 'Current' : formatUTCDetailedDate(s.utilityEndDate ?? undefined)}
                        </div>
                        {s.isActiveAtUtility ? (
                          <div className="text-green-600 dark:text-green-400 text-xs font-medium inline-block">
                            <CheckCircleIcon className="w-5 inline-block mr-1" />
                            Active at utility
                          </div>
                        ) : (
                          <div className="text-red-600 dark:text-red-400 text-xs font-medium inline-block">
                            <XCircleIcon className="w-5 inline-block mr-1" />
                            Inactive at utility
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                  wildCardDisabled={true}
                />
              </div>
            )}
          </div>

          {actionedSubmissions.length > 0 ? (
            renderAllocationDetails(
              selectedActionedAllocation ?? actionedSubmissions[0])
          ) : (
            <Panel header="">
              <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                No actioned VNEM allocation table submissions found
              </div>
            </Panel>
          )}
        </div>

        {/* Non-Actioned Submissions */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="font-semibold text-md my-4">Non-Actioned VNEM Allocation Table Submission</div>
            <div className="flex flex-col justify-end">
              {nonActionedSubmissions.length > 1 && (
                <div className="mb-2 max-w">
                  <ComboboxAutocomplete
                    placeholder="Select a submission"
                    value={selectedNonActionedSubmission?.cuid ?? ''}
                    handleChange={(value) => {
                      const selected = nonActionedSubmissions?.find((alloc) => alloc.cuid === value)
                      setSelectedNonActionedSubmission(selected ?? null)
                    }}
                    data={nonActionedSubmissions.map((s) => ({
                      id: s.cuid,
                      name: formatUTCDetailedDate(new Date(s.submissionDate))
                    }))}
                    wildCardDisabled={true}
                  />
                </div>
              )}
            </div>
          </div>

          {nonActionedSubmissions.length > 0 ? (
            renderAllocationDetails(
              selectedNonActionedSubmission ?? nonActionedSubmissions[0],
              {
                canActivate: programBillingOn && hasActivatePermission,
                programBillingOn,
                hasActivatePermission
              }
            )
          ) : (
            <Panel header="">
              <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                No non-actioned VNEM allocation table submissions found
              </div>
            </Panel>
          )}
        </div>
      </div>

      {orphanMeters.length > 0 && (
        <div className="mt-6">
          <Panel header="Program meters not in this submission">
            <Table
              colConfig={orphanMeterColConfig}
              data={{
                results: orphanMeters.map((m) => ({ ...m, id: m.cuid })),
                total: orphanMeters.length,
                page: 0,
                resultsPerPage: orphanMeters.length,
              }}
              hidePaginator
            />
          </Panel>
        </div>
      )}

      {datesModal != null && (
        <ProgramMeterDatesModal
          isOpen={datesModal != null}
          mode={datesModal.mode}
          meterOptions={meterOptions}
          editingRow={datesModal.row}
          initialBenefittingMeterCuid={datesModal.initialBenefittingMeterCuid}
          programId={programId}
          onClose={() => setDatesModal(null)}
          onSaved={() => void fetchMeters()}
        />
      )}

      {retireMeterRow != null && (
        <RetireProgramMeterModal
          isOpen={retireMeterRow != null}
          row={retireMeterRow}
          programId={programId}
          onClose={() => setRetireMeterRow(null)}
          onRetired={() => void fetchMeters()}
        />
      )}

      {editingPercentages != null && (
        <EditVNEMPercentagesModal
          isOpen={editingPercentages != null}
          programId={programId}
          submission={editingPercentages}
          onClose={() => setEditingPercentages(null)}
          onSaved={() => {
            setEditingPercentages(null)
            void fetchAllocations()
          }}
        />
      )}
    </>
  )
}

export default VNEMAllocationManager
